import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_ATTACHMENT_STYLE,
  PERSONALITY_TEST_ASSESSMENT_ID,
  traitTitleKeyMap,
} from "../../utils/general-config";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import SendToFriendButton from "../../components/send-to-friend-button";
import ShareButton from "../../components/share-button";
import {
  getAttachmentStyleReportPageImage,
  getCookie,
  getEnneagramBannerImage,
  snakeToTitleCase,
} from "../../utils/utility-functions";

import ExpandableSection from "../../components/expandable-collapsible/component";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const AttachmentStyleReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_ATTACHMENT_STYLE[currentLocale] ||
            MODEL_TYPE_ATTACHMENT_STYLE[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const oneAttachment = contentData?.oneAttachmentStyle;
  const twoAttachment = contentData?.twoAttachmentStyles;
  const threeAttachment = contentData?.threeAttachmentStyles;
  const fourAttachment = contentData?.fourAttachmentStyles;

  let updatedDominantTraitData = {};
  const bannerImage = getEnneagramBannerImage();
  const recommendations = contentData?.recommendations;
  const closingNote = contentData?.closingNote;
  const result1ImageText = "attachment-style-1";
  const result2ImageText = "attachment-style-2";
  const result3ImageText = "attachment-style-3";
  const result1Image = getAttachmentStyleReportPageImage(result1ImageText);
  const result2Image = getAttachmentStyleReportPageImage(result2ImageText);
  const result3Image = getAttachmentStyleReportPageImage(result3ImageText);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const progressBarColor = [
    "#ff6284",
    "#49A2E6",
    "#805bec",
    "#ffce55",
    "#4cc0c0",
    "#ec6af9",
  ];

  const colors = [
    "#eef4fe",
    "#f8f4fe",
    "#fdf6eb",
    "#ebfbeb",
    "#feeded",
    "#c7edf9",
    "#e7feeb",
    "#eff4fd",
    "#eae2fd",
  ];

  const descriptiveCardColorMap = [
    { bg: "bg-blue-50", text: "text-blue-800" },
    { bg: "bg-purple-50", text: "text-purple-800" },
    { bg: "bg-amber-50", text: "text-amber-800" },
    { bg: "bg-green-50", text: "text-green-800" },
    { bg: "bg-pink-50", text: "text-pink-800" },
    { bg: "bg-sky-50", text: "text-sky-800" },
    { bg: "bg-emerald-50", text: "text-emerald-800" },
    { bg: "bg-blue-50", text: "text-blue-800" },
    { bg: "bg-violet-50", text: "text-violet-800" },
  ];

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}

        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600 `}
      >
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
          loading="lazy"
        />
        <div
          className={` block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        <div className="relative h-full flex items-center">
          <div className="w-full px-4 lg:px-8 py-8 md:py-12">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4 pr-10 md:pr-16">
              <h2
                className="text-4xl md:text-4xl font-bold text-white leading-tight "
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
              >
                {title}
              </h2>

              {buttonNeeded && (
                <div className="flex-shrink-0 ">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSection = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.testMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.discoverMessage")}
            </p>
            <TakeTestButton onClick={onClick1} />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.bigFiveMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.strengthMesssge")}
            </p>
            <TakeTestButton onClick={onClick2} />
          </div>
        </div>
      </section>
    );
  };

  const InfoListCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const TipsCard = ({
    heading,
    bgClr,
    strengths,
    challenges,
    strengthHeading,
    challengeHeading,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:px-8 md:py-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                  <CircleIcon character={"i"} size={20} />
                </div>

                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        <div className="pl-9">
          {strengthHeading && (
            <h2 className="text-lg font-semibold text-gray-700 my-4">
              {strengthHeading}
            </h2>
          )}

          {strengths && (
            <ul className="space-y-2 mb-4 pl-1">
              {strengths?.map((item, index) => {
                return (
                  <li key={index} className="flex gap-4">
                    <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                      <CircleIcon character={""} size={16} />
                    </div>

                    <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                      {item}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}

          {challengeHeading && (
            <h2 className="text-lg font-semibold text-gray-700 my-4">
              {challengeHeading}
            </h2>
          )}

          {challenges && (
            <ul className="space-y-2 mb-4 pl-1">
              {challenges?.map((item, index) => {
                return (
                  <li key={index} className="flex gap-4">
                    <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                      <CircleIcon character={""} size={16} />
                    </div>

                    <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                      {item}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const ProgressBar = ({ label, percentage, color }) => {
    return (
      <li>
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="text-sm" style={{ color }}>
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          ></div>
        </div>
      </li>
    );
  };

  const HighlightText = ({ text, wordsToHighlight }) => {
    const highlightWords = (str, wordsToHighlight) => {
      return str?.split(" ").map((word, index) => {
        const cleanWord = word.replace(/[^a-zA-Z]/g, ""); // Remove punctuation for accurate matching
        return wordsToHighlight.includes(cleanWord) ? (
          <strong key={index} className="text-[#805fe6] text-2xl">
            {word}{" "}
          </strong>
        ) : (
          <span key={index} className="text-2xl">
            {word}{" "}
          </span>
        );
      });
    };

    return (
      <h3 className="text-base mb-4">
        {highlightWords(text, wordsToHighlight)}
      </h3>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
        style={{ filter: "drop-shadow(0 0 5px rgba(0,0,0,0.3))" }}
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          stroke="none"
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  function transformUserTraitsData(data) {
    if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

    const result = data.reduce((acc, item) => {
      if (
        item?.trait?.title &&
        typeof item.percentage === "number" &&
        typeof item.score === "number"
      ) {
        acc[item.trait.title] = {
          percentage: item.percentage,
          score: item.score,
        };
      }
      return acc;
    }, {});

    const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

    return { result, totalScore };
  }

  const updatedUserTraits = transformUserTraitsData(userTraits);

  function getDominantTraits(userTraits) {
    if (
      !userTraits ||
      !userTraits.result ||
      typeof userTraits.result !== "object"
    ) {
      return null;
    }

    const traits = userTraits.result;
    const sortedTraits = Object.entries(traits).sort(
      (a, b) => b[1].score - a[1].score
    );

    if (sortedTraits.length === 0) return null;

    const extractFirstWord = (traitKey) => traitKey.split("_")[0];

    const dominantTraits = [extractFirstWord(sortedTraits[0][0])];
    let traitCount = 1;
    let baseScore = sortedTraits[0][1].score;

    for (let i = 1; i < sortedTraits.length; i++) {
      if (Math.abs(baseScore - sortedTraits[i][1].score) <= 0.2) {
        dominantTraits.push(extractFirstWord(sortedTraits[i][0]));
        traitCount++;
      } else {
        break;
      }
    }

    return {
      dominantTraits,
      multipleDominant: traitCount > 1,
      traitCount,
    };
  }

  function getMatchingAttachment(key, attachmentStyle) {
    if (attachmentStyle?.content[key]) {
      return {
        RootHeading: attachmentStyle?.heading,
        ...attachmentStyle?.content[key],
      };
    }
    return null;
  }

  const dominantTraits = getDominantTraits(updatedUserTraits);
  const isMultiple = dominantTraits?.multipleDominant;

  const capitalizeFirstChar = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  if (isMultiple) {
    if (dominantTraits?.traitCount === 2) {
      const str1 = dominantTraits?.dominantTraits[0];
      const str2 = dominantTraits?.dominantTraits[1];

      const key1 = str1 + capitalizeFirstChar(str2);
      const key2 = str2 + capitalizeFirstChar(str1);

      updatedDominantTraitData =
        getMatchingAttachment(key1, twoAttachment) ||
        getMatchingAttachment(key2, twoAttachment);
    } else if (dominantTraits?.traitCount === 3) {
      const [str1, str2, str3] = dominantTraits?.dominantTraits;

      const possibleKeys = [
        str1 + capitalizeFirstChar(str2) + capitalizeFirstChar(str3),
        str1 + capitalizeFirstChar(str3) + capitalizeFirstChar(str2),
        str2 + capitalizeFirstChar(str1) + capitalizeFirstChar(str3),
        str2 + capitalizeFirstChar(str3) + capitalizeFirstChar(str1),
        str3 + capitalizeFirstChar(str1) + capitalizeFirstChar(str2),
        str3 + capitalizeFirstChar(str2) + capitalizeFirstChar(str1),
      ];

      for (const key of possibleKeys) {
        updatedDominantTraitData = getMatchingAttachment(key, threeAttachment);
        if (updatedDominantTraitData) break;
      }
    } else if (dominantTraits?.traitCount === 4) {
      const [str1, str2, str3, str4] = dominantTraits?.dominantTraits;

      const possibleKeys = [
        str1 +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str4),
        str1 +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str3),
        str1 +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str4),
        str1 +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str2),
        str1 +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str3),
        str1 +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str2),

        str2 +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str4),
        str2 +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str3),
        str2 +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str4),
        str2 +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str1),
        str2 +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str3),
        str2 +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str1),

        str3 +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str4),
        str3 +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str2),
        str3 +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str4),
        str3 +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str1),
        str3 +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str2),
        str3 +
          capitalizeFirstChar(str4) +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str1),

        str4 +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str3),
        str4 +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str2),
        str4 +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str3),
        str4 +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str1),
        str4 +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str1) +
          capitalizeFirstChar(str2),
        str4 +
          capitalizeFirstChar(str3) +
          capitalizeFirstChar(str2) +
          capitalizeFirstChar(str1),
      ];

      for (const key of possibleKeys) {
        updatedDominantTraitData = getMatchingAttachment(key, fourAttachment);
        if (updatedDominantTraitData) break;
      }
    }
  } else {
    const key = dominantTraits?.dominantTraits[0];
    updatedDominantTraitData = getMatchingAttachment(key, oneAttachment);
  }

  const attachmentStyleScoreList = userTraits.map((item, index) => {
    const localeKey =
      traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];

    return {
      label: snakeToTitleCase(item?.trait[localeKey]),
      score: item.score,
      percentage: item?.percentage,
      clr: progressBarColor[index % progressBarColor.length],
    };
  });

  const otherAttachmentStyle = isMultiple
    ? updatedDominantTraitData?.content?.otherAttachmentStyles
    : updatedDominantTraitData?.yourOtherAttachmentStyles;

  const generalNotes = contentData?.generalNotes;

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData?.header} color="blue" />
                  }
                >
                  {token && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-8 items-center">
                      {/* Text Section (Wider) */}
                      <div className="flex-1">
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                          {contentData?.subHeader}
                        </p>
                        <ul className="space-y-2">
                          {attachmentStyleScoreList?.map((item) => (
                            <ProgressBar
                              color={item?.clr}
                              label={item?.label}
                              percentage={item?.percentage}
                            />
                          ))}
                        </ul>
                      </div>

                      {/* Image Section (Smaller) */}
                      <div className="flex items-cente justify-center">
                        <img
                          src={result1Image}
                          alt="BannerImage"
                          className="w-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={updatedDominantTraitData?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-8 items-center">
                      {/* Text Section (Wider) */}
                      <div className="flex-1">
                        <HighlightText
                          text={
                            isMultiple
                              ? updatedDominantTraitData?.content?.flavorText
                              : updatedDominantTraitData?.flavorText
                          }
                          wordsToHighlight={dominantTraits?.dominantTraits}
                        />
                        <h2 className="text-lg font-semibold text-gray-700 mb-4">
                          {isMultiple
                            ? updatedDominantTraitData?.content?.whatItMeans
                                ?.heading
                            : updatedDominantTraitData?.whatItMeans?.heading}
                        </h2>
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                          {isMultiple
                            ? updatedDominantTraitData?.content?.whatItMeans
                                ?.content
                            : updatedDominantTraitData?.whatItMeans?.content}
                        </p>
                      </div>

                      {/* Image Section (Smaller) */}
                      <div className="flex items-center mb-4 md:mb-6 justify-center">
                        <img
                          src={result2Image}
                          alt="BannerImage"
                          className="w-full object-contain"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                      <TipsCard
                        heading={
                          isMultiple
                            ? updatedDominantTraitData?.content?.tips?.heading
                            : updatedDominantTraitData?.tips?.heading
                        }
                        strengths={
                          isMultiple
                            ? updatedDominantTraitData?.content?.tips?.content
                                ?.strenghts?.content
                            : updatedDominantTraitData?.tips?.content?.strenghts
                                ?.content
                        }
                        challenges={
                          isMultiple
                            ? updatedDominantTraitData?.content?.tips?.content
                                ?.challenges?.content
                            : updatedDominantTraitData?.tips?.content
                                ?.challenges?.content
                        }
                        strengthHeading={
                          isMultiple
                            ? updatedDominantTraitData?.content?.tips?.content
                                ?.strenghts?.heading
                            : updatedDominantTraitData?.tips?.content?.strenghts
                                ?.heading
                        }
                        challengeHeading={
                          isMultiple
                            ? updatedDominantTraitData?.content?.tips?.content
                                ?.challenges?.heading
                            : updatedDominantTraitData?.tips?.content
                                ?.challenges?.heading
                        }
                        bgClr={"#c3edfa"}
                      />
                      <InfoListCard
                        character={"?"}
                        heading={
                          isMultiple
                            ? updatedDominantTraitData?.content?.howToGrow
                                ?.heading
                            : updatedDominantTraitData?.howToGrow?.heading
                        }
                        contentArray={
                          isMultiple
                            ? updatedDominantTraitData?.content?.howToGrow
                                ?.content
                            : updatedDominantTraitData?.howToGrow?.content
                        }
                        bgClr={"#e5ffea"}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        otherAttachmentStyle
                          ? otherAttachmentStyle?.heading
                          : generalNotes?.heading
                      }
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    {!isMultiple && (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                        {otherAttachmentStyle?.flavorText}
                      </p>
                    )}
                    <div
                      className={`grid gap-8 items-stretch grid-cols-1 ${
                        dominantTraits?.traitCount === 1
                          ? "md:grid-cols-3"
                          : dominantTraits?.traitCount === 2
                          ? "md:grid-cols-2"
                          : dominantTraits?.traitCount === 3
                          ? "md:grid-cols-1"
                          : "hidden"
                      }`}
                    >
                      {otherAttachmentStyle &&
                        otherAttachmentStyle?.content &&
                        Object.entries(otherAttachmentStyle?.content)
                          .map(([key, value]) => ({
                            type: key,
                            ...value,
                          }))
                          .map((item, index) => {
                            return (
                              <div key={index} className="flex">
                                <DescriptiveCard
                                  heading={item?.heading}
                                  overview={item?.content}
                                  bgClr={
                                    descriptiveCardColorMap[
                                      index % descriptiveCardColorMap.length
                                    ].bg
                                  }
                                  textClr={
                                    descriptiveCardColorMap[
                                      index % descriptiveCardColorMap.length
                                    ].text
                                  }
                                />
                              </div>
                            );
                          })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mt-6">
                      <div>
                        {/* <h2 className="text-xl font-semibold text-gray-700 mb-4">
                          {generalNotes?.heading}
                        </h2> */}
                        {generalNotes && generalNotes?.content && (
                          <ul className="space-y-2 pl-2">
                            {generalNotes?.content?.map((item, index) => {
                              return (
                                <li
                                  key={index}
                                  className="text-sm my-4 text-gray-600 flex items-start gap-2"
                                >
                                  <div className="flex items-start gap-2">
                                    <div>
                                      <div className="flex items-start gap-2">
                                        <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                                          <CircleIcon
                                            character={""}
                                            size={16}
                                          />
                                        </div>
                                        <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                                          {item}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                      {/* Image Section (Smaller) */}
                      <div className="flex items-center justify-center">
                        <img
                          src={result3Image}
                          alt="BannerImage"
                          className="w-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t("report.narcissistic_personality.what_next")}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mt-6">
                      <InfoListCard
                        heading={recommendations?.heading}
                        isSubHeading={true}
                        contentArray={recommendations?.content}
                        character={"i"}
                        bgClr={"#fdf6eb"}
                      />
                      <InfoListCard
                        heading={closingNote?.heading}
                        isSubHeading={true}
                        contentArray={closingNote?.content}
                        character={"?"}
                        overview={closingNote?.flavorText}
                        bgClr={"#eae1ff"}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <CallToActionSection
                onClick1={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
                onClick2={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AttachmentStyleReport;
